import {
  createContext,
  use,
  useId,
  type AriaAttributes,
  type ComponentPropsWithRef,
  type ReactNode,
} from "react";

import { cn } from "../lib/cn";

/**
 * Le câblage d'accessibilité d'un champ de formulaire est répétitif et
 * systématiquement bâclé : `htmlFor` qui ne correspond à rien, message d'erreur
 * invisible aux lecteurs d'écran, `aria-describedby` oublié.
 *
 * `Field` le fait une fois pour toutes. Il génère les identifiants et les
 * distribue par contexte ; le contrôle les récupère via `useFieldControl()` :
 *
 *   <Field label="E-mail" hint="Professionnel de préférence" error={errors.email}>
 *     <Input type="email" />
 *   </Field>
 *
 * Le développeur n'écrit aucun `id`, aucun `aria-*`, et ne peut pas se tromper.
 */
interface FieldContextValue {
  controlId: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * À appeler dans tout contrôle de formulaire du design system.
 * Renvoie les props à étaler sur l'élément natif, ou un objet vide
 * si le contrôle est utilisé hors d'un `Field`.
 */
interface ControlProps {
  id?: string;
  "aria-describedby"?: string;
  // Le type exact de React : `grammar` et `spelling` existent aussi.
  "aria-invalid"?: AriaAttributes["aria-invalid"];
  required?: boolean;
}

// Générique pour que le type de retour conserve toutes les props du contrôle
// (`type`, `placeholder`, `onChange`…) et pas seulement les quatre ci-dessus.
export function useFieldControl<T extends ControlProps>(props: T): T {
  const field = use(FieldContext);
  if (!field) return props;

  return {
    ...props,
    // Une prop explicite l'emporte toujours sur le contexte : le composant
    // reste utilisable dans un cas particulier sans avoir à le forker.
    id: props.id ?? field.controlId,
    "aria-describedby": props["aria-describedby"] ?? field.describedBy,
    "aria-invalid": props["aria-invalid"] ?? (field.invalid || undefined),
    required: props.required ?? field.required,
  };
}

export interface FieldProps extends Omit<ComponentPropsWithRef<"div">, "children"> {
  label?: ReactNode;
  /** Texte d'aide affiché sous le contrôle. Masqué dès qu'une erreur apparaît. */
  hint?: ReactNode;
  /** Message d'erreur. Sa présence bascule le champ en état invalide. */
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
}

export function Field({
  className,
  label,
  hint,
  error,
  required = false,
  children,
  ...props
}: FieldProps) {
  const id = useId();
  const controlId = `${id}-control`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const invalid = Boolean(error);

  // On ne décrit le champ que par le message pertinent : annoncer l'aide ET
  // l'erreur noierait l'information qui compte.
  const describedBy = invalid ? errorId : hint ? hintId : undefined;

  return (
    <FieldContext value={{ controlId, describedBy, invalid, required }}>
      <div className={cn("flex flex-col gap-1.5", className)} {...props}>
        {label && (
          <Label htmlFor={controlId}>
            {label}
            {required && (
              <span className="text-danger" aria-hidden="true">
                {" *"}
              </span>
            )}
          </Label>
        )}

        {children}

        {invalid ? (
          // `role="alert"` fait annoncer le message dès son apparition,
          // sans attendre que l'utilisateur revienne sur le champ.
          <p id={errorId} role="alert" className="text-xs text-danger-fg">
            {error}
          </p>
        ) : (
          hint && (
            <p id={hintId} className="text-xs text-fg-muted">
              {hint}
            </p>
          )
        )}
      </div>
    </FieldContext>
  );
}

export function Label({ className, ...props }: ComponentPropsWithRef<"label">) {
  return (
    <label
      className={cn("text-base font-semibold sm:text-sm sm:font-medium text-fg select-none", className)}
      {...props}
    />
  );
}
